/* eslint-disable no-unused-vars */
import React from "react";
import moment from "moment";
import { SessionRow, sessionEnd, sessionStart } from "../../DeclarationCourse/SessionList";
import { DV2 } from "../theme";
import { clock, dayLabel, hours } from "../format";
import { FieldLabel, fieldStyle, useDv2Theme } from "../layout/Ui";
import { DurationStepper, TimeSelect } from "./TimeFields";
import { timeToIso, timeValue } from "./planningRules";

interface Props {
    row: SessionRow;
    index: number;
    issue?: string | null;
    expanded: boolean;
    onToggle: () => void;
    onChange: (patch: Partial<SessionRow>) => void;
    onRemove?: () => void;
    minDate: string;
}

/**
 * Carte d'une séance (maquettes 1c et 2c) : repliée, une ligne date, début,
 * durée, fin ; dépliée, les champs éditables. L'erreur de la séance s'affiche
 * sur la carte, qui passe en rouge.
 */
const SessionCard = ({ row, index, issue, expanded, onToggle, onChange, onRemove, minDate }: Props) => {
    const { primary, fontFamily } = useDv2Theme();
    const start = sessionStart(row);
    const end = sessionEnd(row);
    const time = timeValue(row.startTime);
    const border = issue ? `2px solid ${DV2.danger}` : expanded ? `2px solid ${primary}` : `1px solid ${DV2.border}`;
    const errorId = `dv2-session-${row.key}-error`;

    const iconButton = (label: string, glyph: string, onClick: () => void, color: string) => (
        <button
            type="button"
            aria-label={label}
            title={label}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            style={{ width: 36, height: 36, borderRadius: 10, background: DV2.pageBg, border: "none", color, cursor: "pointer", display: "grid", placeItems: "center", fontSize: 16, flexShrink: 0 }}
        >
            {glyph}
        </button>
    );
    /** Crayon (modifier) et corbeille (supprimer) : le crayon donne le réflexe du clic, en plus du texte d'aide. */
    const actions = (
        <div style={{ display: "flex", gap: 6 }}>
            {iconButton(expanded ? `Réduire la séance ${index + 1}` : `Modifier la séance ${index + 1}`, "✎", onToggle, primary)}
            {onRemove && iconButton(`Supprimer la séance ${index + 1}`, "🗑", onRemove, DV2.placeholder)}
        </div>
    );

    return (
        <div id={`dv2-session-${row.key}`} style={{ background: "#fff", border, borderRadius: 16, padding: "16px 20px", fontFamily, display: "flex", flexDirection: "column", gap: 12 }}>
            {!expanded ? (
                <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-center">
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-expanded={false}
                        aria-describedby={issue ? errorId : undefined}
                        className="grid grid-cols-2 md:grid-cols-[minmax(0,1.3fr)_1fr_1fr_1fr] gap-3 items-center text-left w-full"
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily, color: DV2.text }}
                    >
                        <div className="col-span-2 md:col-span-1">
                            <div style={{ fontWeight: 800, fontSize: 15 }}>{row.date ? dayLabel(row.date) : "Date à choisir"}</div>
                            <div style={{ fontSize: 12, color: DV2.muted }}>Séance {index + 1}</div>
                        </div>
                        <div><div style={{ fontSize: 12, color: DV2.muted }}>Début</div><div style={{ fontWeight: 800, color: issue ? DV2.danger : DV2.text }}>{time ? time.replace(":", "h") : "à choisir"}</div></div>
                        <div><div style={{ fontSize: 12, color: DV2.muted }}>Durée</div><div style={{ fontWeight: 800 }}>{hours(row.duration)}</div></div>
                        <div><div style={{ fontSize: 12, color: DV2.muted }}>Fin</div><div style={{ fontWeight: 800 }}>{end ? clock(end) : ""}</div></div>
                    </button>
                    {actions}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.3fr)_1fr_1fr_1fr_auto] gap-3 items-end">
                    <div style={{ minWidth: 0 }}>
                        <FieldLabel htmlFor={`dv2-session-${row.key}-date`}>Date, séance {index + 1}</FieldLabel>
                        <input
                            id={`dv2-session-${row.key}-date`}
                            className="dv2-date-input"
                            type="date"
                            value={row.date ?? ""}
                            min={minDate}
                            onChange={(e) => onChange({ date: e.target.value || null, startTime: time && e.target.value ? timeToIso(time, e.target.value) : row.startTime })}
                            aria-invalid={!!issue}
                            aria-describedby={issue ? errorId : undefined}
                            style={{ ...fieldStyle(!!issue), fontFamily, fontSize: 14 }}
                        />
                    </div>
                    <TimeSelect id={`dv2-session-${row.key}-time`} label="Début" value={time} onChange={(value) => onChange({ startTime: timeToIso(value, row.date) })} error={!!issue && !time} describedBy={issue ? errorId : undefined} />
                    <DurationStepper id={`dv2-session-${row.key}-duration`} label="Durée" value={row.duration} onChange={(value) => onChange({ duration: value })} size="sm" />
                    <div style={{ paddingBottom: 9 }}>
                        <div style={{ fontSize: 12, color: DV2.muted }}>Fin</div>
                        <div style={{ fontWeight: 800 }}>{end ? clock(end) : "—".replace("—", "")}</div>
                    </div>
                    <div className="flex items-center justify-between md:block">
                        <button type="button" onClick={onToggle} className="md:hidden" style={{ background: "none", border: "none", color: primary, fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily }}>Réduire</button>
                        {actions}
                    </div>
                </div>
            )}
            {issue && (
                <div id={errorId} role="alert" style={{ fontSize: 13, color: DV2.danger, lineHeight: 1.45, display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <span aria-hidden style={{ width: 18, height: 18, borderRadius: "50%", background: DV2.danger, color: "#fff", fontSize: 11, fontWeight: 800, display: "grid", placeItems: "center", flexShrink: 0 }}>!</span>
                    <span>{issue}{!expanded && <> <button type="button" onClick={onToggle} style={{ background: "none", border: "none", color: DV2.danger, fontWeight: 800, textDecoration: "underline", cursor: "pointer", fontFamily, padding: 0 }}>Corriger</button></>}</span>
                </div>
            )}
            {start && !issue && row.date && row.date < moment().format("YYYY-MM-DD") && (
                <div style={{ fontSize: 12, color: DV2.muted }}>Séance déjà passée.</div>
            )}
        </div>
    );
};

export default SessionCard;
